import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDataSourceGeneric} from "@Services/base/daoService";
import {ModelPjFile, ModelPjFileType, ModelPjType} from "app/dar/common/services/model-pj/model-pj.model";
import {ActionDataTable, ColumnDataTable, DataTableComponent} from "@Common/widgets/general/data-table";
import {ModelPjFileService} from "app/dar/common/services/model-pj/model-pj-file.service";
import {ModelPjTypeService} from "app/dar/common/services/model-pj/model-pj-type.service";
import {PiecejointeService} from "app/dar/common/services/pieces-jointes/piecejointe.service";
import {MatDialog} from "@angular/material/dialog";
import {SubSink} from "@Common/core/utils/subsink";
import {DialogData, DialogValidationComponent} from "@Common/widgets/general/dialog-validation";
import {FilePreviewModaleComponent} from "app/dar/common/file-preview-modale/file-preview-modale.component";
import {
  ModelPjFileEditComponent
} from "app/dar/common/model-pj/model-pj/model-pj-file-edit/model-pj-file-edit.component";

@Component({
  selector: 'app-model-pj-file',
  templateUrl: './model-pj-file.component.html',
  styleUrls: ['./model-pj-file.component.scss']
})
export class ModelPjFileComponent implements OnInit, OnDestroy {
  @Input() downloadOnly: boolean;
  subSink = new SubSink();
  pjTemplateFileSource: MatDataSourceGeneric<ModelPjFile> = new MatDataSourceGeneric<ModelPjFile>();
  @ViewChild(DataTableComponent, {static: true}) dataTable: DataTableComponent;
  @ViewChild('fileUpload', {static: false}) fileUpload: ElementRef;

  columns: ColumnDataTable[] = [
    {
      columnDef: 'template_type__name', header: 'Catégorie',
      display: (element: ModelPjFile) => `${element?.template_type_obj?.name}`,
      sort: true, flex: 15
    },
    {
      columnDef: 'template_name', header: 'Nom',
      display: (element: ModelPjFile) => `${element?.template_name}`,
      sort: true, flex: 70
    },
    {
      columnDef: 'file_name', header: 'Nom du fichier',
      display: (element: ModelPjFile) => `${element.file_name ?? ''}`,
      sort: false, flex: 15
    },
  ];

  actions: ActionDataTable[] = [
    {
      label: 'Editer', tooltip: 'Editer le modèle',
      icon: 'edit',
      hidden: (row: ModelPjFile) => this.downloadOnly,
      click: (row: ModelPjFile) => this.editModel(row)
    },

    {
      label: 'Up/Down', tooltip: '',
      tooltipfct: (row) => {
        if (this.fileExist(row)) {
          return 'télécharger le fichier';
        }
        return 'téléverser / mettre un fichier';
      },
      icon: 'upload', iconfct: (row: ModelPjFile) => this.fileExist(row) ? 'download' : 'upload',
      iconcolor: 'warn',
      click: (row: ModelPjFile) => this.updownload(row)
    },
    {
      label: 'Preview', tooltip: 'Prévisualiser le modèle',
      icon: 'preview',
      iconcolor: 'warn',
      hidden: (row: ModelPjFile) => this.previewDisabled(row),
      click: (row: ModelPjFile) => this.preview(row)
    },
    {
      label: 'Supprimer', tooltip: '',
      icon: 'delete', iconcolor: 'warn',
      hidden: (row: ModelPjFile) => !this.fileExist(row) || this.downloadOnly,
      click: (row: ModelPjFile) => this.delete(row)
    },
  ];
  extraParams: Map<string, string> = new Map<string, string>();
  requiredFileType = '*';
  currentModelPj: ModelPjFile = null;

  typeList: ModelPjType[] = null;
  hasFiles = true;
  constructor(
    private pjTemplateFileSvc: ModelPjFileService,
    private pjTemplateTypeSvc: ModelPjTypeService,
    private pieceJointeService: PiecejointeService,
    private dialog: MatDialog,
  ) {
    this.pjTemplateFileSource.daoService = pjTemplateFileSvc;
  }

  ngOnInit(): void {
    if (this.downloadOnly) {
      this.extraParams.set('file_name__isnull', false.toString());
    }
    this.subSink.sink = this.pjTemplateTypeSvc.getList().subscribe(data => {
      this.typeList = data;
      this.dataTable.reload();
    });
    this.subSink.sink = this.pjTemplateFileSource.listCountTotal$.subscribe(_cnt => this.hasFiles = _cnt > 0);
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  private fileExist(row: ModelPjFile) {
    return (row.file_name ?? '').length > 0;
  }

  private download(row: ModelPjFile) {
    this.pieceJointeService.downloadModelPj(row);
  }

  private delete(row: ModelPjFile) {
    const data = new DialogData();
    data.title = 'Confirmation de suppression de fichier';
    data.message = `Souhaitez vous supprimer le modèle (${row.file_name}) ? `;

    const dialogRef = this.dialog.open(DialogValidationComponent, {data: data});
    dialogRef.updatePosition({top: '40px'});
    this.subSink.sink = dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.pjTemplateFileSvc.deleteModel(row).subscribe(() => this.dataTable.reload());
      }
    });
  }

  private preview(row: ModelPjFile) {
    this.pieceJointeService.getFile(row.id, 'AttachmentTemplateFile').subscribe(
      data => {
        if (data) {
          this.dialog.open(FilePreviewModaleComponent, {data: data});
        }
      });
  }

  private previewDisabled(row: ModelPjFile) {
    if (this.fileExist(row)) {
      const name = row.file_name.toLowerCase();
      for (const ext of ['png', 'jpg', 'jpeg', 'pdf']) {
        if (name.endsWith(ext)) {
          return false;
        }
      }
    }
    return true;
  }

  private upload(row: ModelPjFile) {
    this.currentModelPj = row;
    this.fileUpload.nativeElement.click();
  }

  private updownload(row: ModelPjFile) {
    if (this.fileExist(row)) {
      this.download(row);
    } else {
      this.upload(row);
    }
  }

  onFileSelected(event) {
    const file: File = event.target.files[0];
    this.pjTemplateFileSvc.uploadModel(this.currentModelPj, file).subscribe(() => this.dataTable.reload());
    this.currentModelPj = null;
  }

  private editModel(row: ModelPjFile) {
    const data: ModelPjFileType = {
      modelFile: row,
      modelTypes: this.typeList
    };
    const dialogRef = this.dialog.open(ModelPjFileEditComponent, {data: data});
    dialogRef.updateSize('700px');
    dialogRef.updatePosition({top: '50px'});
    this.subSink.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.saveModel(result);
      }
    });
  }

  private saveModel(data: ModelPjFile) {
    this.subSink.sink = this.pjTemplateFileSvc.updateModel(data).subscribe((res) => this.dataTable.reload());
  }

  addModel() {
    this.editModel({});
  }
}
